import { useState, useEffect, useCallback } from 'react';
import { Popover, Text, Box, Link, Icon, Tag } from '@nimbus-ds/components';
import { CloseIcon } from '@nimbus-ds/icons';
import { useNavegate } from 'App/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { partnersAppsRoutes } from 'domains/PartnersApps';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import useUpdateNuvemEnvioConfigs from 'domains/Shipping/DeliveryMethods/hooks/useUpdateNuvemEnvioConfigs';
import { NE_DISPLAY_SWITCHES } from '../constants';
import {
  trackingNuvemEnvioPopoverUnpackedView,
  trackingNuvemEnvioPopoverUnpackedClose,
  trackingNuvemEnvioPopoverUnpackedClick,
} from '../tracking';

interface PopoverUnpackedProps {
  children: React.ReactNode;
  isSelected: boolean;
}
function PopoverUnpacked({ children, isSelected }: PopoverUnpackedProps) {
  const t = useTranslationOrders();
  const { id: storeId } = useStoreInfo();

  const { fetchNuvemEnvioConfigs, nuvemEnvioConfigs } =
    useGetNuvemEnvioConfigs();

  const [isVisible, setIsVisible] = useState(false);
  const { updateNuvemEnvioDisplaySwitches } = useUpdateNuvemEnvioConfigs();

  const { goTo } = useNavegate();

  const onRedirect = useCallback(() => {
    goTo(`${partnersAppsRoutes.nuvemenvio}/#/dashboard?saveOnShipping=true`);
  }, [goTo]);

  const handleTrackingView = useCallback(() => {
    trackingNuvemEnvioPopoverUnpackedView(storeId);
  }, [storeId]);

  useEffect(() => {
    if (
      isSelected &&
      nuvemEnvioConfigs?.displaySwitches?.showNeNudgeInOrdersPopover
    ) {
      setIsVisible(true);
      handleTrackingView();
    } else {
      setIsVisible(false);
    }
  }, [
    isSelected,
    nuvemEnvioConfigs?.displaySwitches?.showNeNudgeInOrdersPopover,
    handleTrackingView,
  ]);

  const handleVisibility = () => {
    setIsVisible(
      nuvemEnvioConfigs?.displaySwitches?.showNeNudgeInOrdersPopover || false,
    );
  };

  const handleClose = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setIsVisible(false);
    trackingNuvemEnvioPopoverUnpackedClose(storeId);
    await updateNuvemEnvioDisplaySwitches(
      NE_DISPLAY_SWITCHES.SHOW_NE_NUDGE_IN_ORDERS_POPOVER,
    );
    fetchNuvemEnvioConfigs();
  };

  const handleRedirect = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    trackingNuvemEnvioPopoverUnpackedClick(storeId);
    onRedirect();
  };

  return (
    <Popover
      position="bottom-start"
      visible={isVisible}
      onVisibility={handleVisibility}
      content={
        <Box
          width="280px"
          backgroundColor="transparent"
          gap="2"
          display="flex"
          flexDirection="column"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Tag appearance="primary">
              {t('nuvemEnvio.popover-unpacked.tag')}
            </Tag>

            <Icon
              source={<CloseIcon />}
              color="neutral-textHigh"
              onClick={handleClose}
              style={{ cursor: 'pointer' }}
            />
          </Box>
          <Text fontSize="caption" color="neutral-textDisabled">
            {t('nuvemEnvio.popover-unpacked.text')}
          </Text>
          <Link
            as="button"
            onClick={handleRedirect}
            appearance="primary"
            fontSize="caption"
            textDecoration="none"
          >
            {t('nuvemEnvio.popover-unpacked.link')}
          </Link>
        </Box>
      }
    >
      {children}
    </Popover>
  );
}

export default PopoverUnpacked;
